import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import BillingService from '../Billing/billing-service';
import { useBilling } from '@/components/Billing/Billing.composables';
import { ProductType, PurchaseDetails, ProductInvoicePreview } from '@/components/Billing/billing-types';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
// @ts-ignore
import PriceBreakdownSection from '@/components/dialogs/PricingBreakdownDialog/PriceBreakdownSection.vue';
import { getReadableAmountInDollars } from '@/utils/helpers';

interface InvoicesPreviewsBy {
	new: ProductInvoicePreview | undefined;
	current: ProductInvoicePreview | undefined;
}
interface PriceBreakdownItem {
	from?: { title: string; price?: string };
	to?: { title: string; price?: string };
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PricingBreakdownDialog',
  props: {
    newPurchaseDetails: null,
    currentPurchaseDetails: null,
    dialogPromiseHandlers: null
  },
  setup(__props: any) {

const props = __props as {
	newPurchaseDetails: PurchaseDetails;
	currentPurchaseDetails: PurchaseDetails;
	dialogPromiseHandlers: { resolve: () => void; reject: () => void };
}



const { usePricingPreview, billingPeriod } = useBilling();

// Team features
const newTeamFeaturePlanId = computed(() => props.newPurchaseDetails.teamFeature.id);
const teamFeaturesPricePreviewInputParams = computed(() => ({
	planId: newTeamFeaturePlanId.value,
	billingPeriod: billingPeriod.value,
	productId: ProductType.TeamFeatures,
}));
const currentTeamFeaturePlanId = computed(() => props.currentPurchaseDetails.teamFeature.id);
const currentTeamFeaturesPricePreviewInputParams = computed(() => ({
	planId: currentTeamFeaturePlanId.value,
	billingPeriod: billingPeriod.value,
	productId: ProductType.TeamFeatures,
}));
const didTeamFeaturesChange = computed(() => newTeamFeaturePlanId.value !== currentTeamFeaturePlanId.value);
const newTeamFeaturesInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(teamFeaturesPricePreviewInputParams.value))
);
const currentTeamFeaturesInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(currentTeamFeaturesPricePreviewInputParams.value))
);
// Customer features
const newCustomerFeaturePlanId = computed(() => props.newPurchaseDetails.customerFeature.id);
const newCustomerFeatureQuantity = computed(() => props.newPurchaseDetails.customerFeature.quantity);
const currentCustomerFeaturePlanId = computed(() => props.currentPurchaseDetails.customerFeature.id);
const currentCustomerFeatureQuantity = computed(() => props.currentPurchaseDetails.customerFeature.quantity);

const newCustomerFeaturesPricePreviewInputParams = computed(() => ({
	billingPeriod: billingPeriod.value,
	planId: newCustomerFeaturePlanId.value,
	productId: ProductType.CustomerFeatures,
	unitQuantity: newCustomerFeatureQuantity.value,
}));
const currentCustomerFeaturesPricePreviewInputParams = computed(() => ({
	billingPeriod: billingPeriod.value,
	planId: currentCustomerFeaturePlanId.value,
	productId: ProductType.CustomerFeatures,
	unitQuantity: currentCustomerFeatureQuantity.value,
}));
const newCustomerFeaturesInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(newCustomerFeaturesPricePreviewInputParams.value))
);
const currentCustomerFeaturesInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(currentCustomerFeaturesPricePreviewInputParams.value))
);

const didCustomerFeaturesChange = computed(
	() =>
		newCustomerFeaturePlanId.value !== currentCustomerFeaturePlanId.value ||
		newCustomerFeatureQuantity.value !== currentCustomerFeatureQuantity.value
);
// Automation
const newAutomationQuantity = computed(() => props.newPurchaseDetails.automationTasks.quantity);
const currentAutomationQuantity = computed(() => props.currentPurchaseDetails.automationTasks.quantity);
const newAutomationPricePreviewInputParams = computed(() => ({
	productId: ProductType.Automation,
	billingPeriod: billingPeriod.value,
	unitQuantity: newAutomationQuantity.value,
}));
const currentAutomationPricePreviewInputParams = computed(() => ({
	productId: ProductType.Automation,
	billingPeriod: billingPeriod.value,
	unitQuantity: currentAutomationQuantity.value,
}));
const newAutomationInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(newAutomationPricePreviewInputParams.value))
);
const currentAutomationInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(currentAutomationPricePreviewInputParams.value))
);
const didAutomationChange = computed(() => newAutomationQuantity.value !== currentAutomationQuantity.value);
// Ply credits
const newPlyCreditsQuantity = computed(() => props.newPurchaseDetails.credits.quantity);
const currentPlyCreditsQuantity = computed(() => props.currentPurchaseDetails.credits.quantity);
const newPlyCreditsPricePreviewInputParams = computed(() => ({
	productId: ProductType.Credits,
	billingPeriod: billingPeriod.value,
	unitQuantity: newPlyCreditsQuantity.value,
}));
const currentPlyCreditsPricePreviewInputParams = computed(() => ({
	productId: ProductType.Credits,
	billingPeriod: billingPeriod.value,
	unitQuantity: currentPlyCreditsQuantity.value,
}));
const newPlyCreditsInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(newPlyCreditsPricePreviewInputParams.value))
);
const currentPlyCreditsInvoicePreview = computed(() =>
	usePricingPreview.value(BillingService.buildPricePreviewInput(currentPlyCreditsPricePreviewInputParams.value))
);

const didPlyCreditsChange = computed(() => newPlyCreditsQuantity.value !== currentPlyCreditsQuantity.value);

const teamFeaturesPrices = computed(() => BillingService.getProductPricePreview(newTeamFeaturesInvoicePreview.value));
const customerFeaturesPrices = computed(() => BillingService.getProductPricePreview(newCustomerFeaturesInvoicePreview.value));
const automationPrices = computed(() => BillingService.getProductPricePreview(newAutomationInvoicePreview.value));
const plyCreditsPrices = computed(() => BillingService.getProductPricePreview(newPlyCreditsInvoicePreview.value));

const totalDueToday = computed(() => {
	const teamFeaturesDueToday = teamFeaturesPrices.value.dueToday;
	const customerFeaturesDueToday = customerFeaturesPrices.value.dueToday;
	const automationDueToday = automationPrices.value.dueToday;
	const plyCreditsDueToday = plyCreditsPrices.value.dueToday;
	return teamFeaturesDueToday + customerFeaturesDueToday + automationDueToday + plyCreditsDueToday;
});
const readableTotalDueToday = computed(() => getReadableAmountInDollars(totalDueToday.value));

const allRelevantInvoicePreviews = computed(() => {
	const didBillingPeriodChange = props.newPurchaseDetails.billingPeriod !== props.currentPurchaseDetails.billingPeriod;
	const teamFeaturesPreviews = {
		new: newTeamFeaturesInvoicePreview.value,
		current: currentTeamFeaturesInvoicePreview.value,
	};
	const customerFeaturesPreviews = {
		new: newCustomerFeaturesInvoicePreview.value,
		current: currentCustomerFeaturesInvoicePreview.value,
	};
	const automationPreviews = {
		new: newAutomationInvoicePreview.value,
		current: currentAutomationInvoicePreview.value,
	};
	const plyCreditsPreviews = {
		new: newPlyCreditsInvoicePreview.value,
		current: currentPlyCreditsInvoicePreview.value,
	};

	if (didBillingPeriodChange) {
		return {
			[ProductType.TeamFeatures]: teamFeaturesPreviews,
			[ProductType.CustomerFeatures]: customerFeaturesPreviews,
			[ProductType.Automation]: automationPreviews,
			[ProductType.Credits]: plyCreditsPreviews,
		};
	} else {
		// @ts-ignore
		const res: Record<ProductType, InvoicesPreviewsBy> = {};
		if (didTeamFeaturesChange.value) {
			res[ProductType.TeamFeatures] = teamFeaturesPreviews;
		}
		if (didCustomerFeaturesChange.value) {
			res[ProductType.CustomerFeatures] = customerFeaturesPreviews;
		}
		if (didAutomationChange.value) {
			res[ProductType.Automation] = automationPreviews;
		}
		if (didPlyCreditsChange.value) {
			res[ProductType.Credits] = plyCreditsPreviews;
		}
		return res;
	}
});

const upgradedItems = computed(() => {
	// @ts-ignore
	const res: Record<ProductType, InvoicesPreviewsBy> = {};
	const allRelevantInvoicePreviewsKeys = Object.keys(allRelevantInvoicePreviews.value) as ProductType[];
	allRelevantInvoicePreviewsKeys.forEach((productType) => {
		const productPreviews = allRelevantInvoicePreviews.value[productType];
		const newInvoicePreview = productPreviews.new;
		if (newInvoicePreview && !newInvoicePreview.preview_subscription.is_plan_downgrade) {
			res[productType] = allRelevantInvoicePreviews.value[productType];
		}
	});
	return res;
});
const getInvoicePreviewNewAmount = (invoicePreview: ProductInvoicePreview | undefined) =>
	invoicePreview?.preview_subscription.immediate_invoice.total.amount || 0;
const newItems = computed(() => {
	// @ts-ignore
	const res: Record<ProductType, InvoicesPreviewsBy> = {};
	const allRelevantInvoicePreviewsKeys = Object.keys(upgradedItems.value) as ProductType[];
	allRelevantInvoicePreviewsKeys.forEach((productType) => {
		const productPreviews = upgradedItems.value[productType];
		const newInvoicePreview = productPreviews.new;
		const currentInvoicePreview = productPreviews.current;
		if (getInvoicePreviewNewAmount(newInvoicePreview) > 0 && getInvoicePreviewNewAmount(currentInvoicePreview) === 0) {
			res[productType] = upgradedItems.value[productType];
		}
	});
	return res;
});

const downgradedItems = computed(() => {
	// @ts-ignore
	const res: Record<ProductType, InvoicesPreviewsBy> = {};
	const allRelevantInvoicePreviewsKeys = Object.keys(allRelevantInvoicePreviews.value) as ProductType[];
	allRelevantInvoicePreviewsKeys.forEach((productType) => {
		const productPreviews = allRelevantInvoicePreviews.value[productType];
		const newInvoicePreview = productPreviews.new;
		if (newInvoicePreview && newInvoicePreview.preview_subscription.is_plan_downgrade) {
			res[productType] = allRelevantInvoicePreviews.value[productType];
		}
	});
	return res;
});
const cancelledItems = computed(() => {
	// @ts-ignore
	const res: Record<ProductType, InvoicesPreviewsBy> = {};
	const allRelevantInvoicePreviewsKeys = Object.keys(downgradedItems.value) as ProductType[];
	allRelevantInvoicePreviewsKeys.forEach((productType) => {
		const productPreviews = downgradedItems.value[productType];
		const newInvoicePreview = productPreviews.new;
		if (getInvoicePreviewNewAmount(newInvoicePreview) === 0) {
			res[productType] = downgradedItems.value[productType];
		}
	});
	return res;
});

const planNamesByProductType = computed(() => {
	const res: Record<ProductType, { current: string; new: string }> = {
		[ProductType.TeamFeatures]: {
			new: BillingService.getPlanReadableName({ planId: newTeamFeaturePlanId.value, productType: ProductType.TeamFeatures }),
			current: BillingService.getPlanReadableName({ planId: currentTeamFeaturePlanId.value, productType: ProductType.TeamFeatures }),
		},
		[ProductType.CustomerFeatures]: {
			new: BillingService.getPlanReadableName({
				planId: newCustomerFeaturePlanId.value,
				productType: ProductType.CustomerFeatures,
				quantity: newCustomerFeatureQuantity.value,
			}),
			current: BillingService.getPlanReadableName({
				productType: ProductType.CustomerFeatures,
				planId: currentCustomerFeaturePlanId.value,
				quantity: currentCustomerFeatureQuantity.value,
			}),
		},
		[ProductType.Automation]: {
			new: BillingService.getPlanReadableName({ quantity: newAutomationQuantity.value, productType: ProductType.Automation }),
			current: BillingService.getPlanReadableName({ quantity: currentAutomationQuantity.value, productType: ProductType.Automation }),
		},

		[ProductType.Credits]: {
			new: BillingService.getPlanReadableName({ quantity: newPlyCreditsQuantity.value, productType: ProductType.Credits }),
			current: BillingService.getPlanReadableName({ quantity: currentPlyCreditsQuantity.value, productType: ProductType.Credits }),
		},
	};
	return res;
});

const upgradedItemsDisplay = computed(() => {
	const res: PriceBreakdownItem[] = [];
	const newProducts = Object.keys(newItems.value) as ProductType[];
	const upgradedProducts = Object.keys(upgradedItems.value) as ProductType[];
	const relevantUpgradedProducts = upgradedProducts.filter((productType) => !newProducts.includes(productType));
	relevantUpgradedProducts.forEach((productType) => {
		const productPreviews = upgradedItems.value[productType];
		const newInvoicePreview = productPreviews.new;
		// const currentInvoicePreview = productPreviews.current;
		const newAmount = newInvoicePreview?.preview_subscription.immediate_invoice.total.amount || 0;
		// const currentAmount = getInvoicePreviewNewAmount(currentInvoicePreview);
		res.push({
			from: {
				title: `From: ${planNamesByProductType.value[productType].current}`,
				// , price: `-${getReadableAmountInDollars(currentAmount)}`
			},
			to: { title: `To: ${planNamesByProductType.value[productType].new}`, price: getReadableAmountInDollars(newAmount) },
		});
	});
	return res;
});

const newItemsDisplay = computed(() => {
	const res: PriceBreakdownItem[] = [];
	const newProducts = Object.keys(newItems.value) as ProductType[];
	newProducts.forEach((productType) => {
		const productPreviews = newItems.value[productType];
		const newInvoicePreview = productPreviews.new;
		const newAmount = getInvoicePreviewNewAmount(newInvoicePreview);
		res.push({ to: { title: planNamesByProductType.value[productType].new, price: getReadableAmountInDollars(newAmount) } });
	});
	return res;
});

const downgradedItemsDisplay = computed(() => {
	const res: PriceBreakdownItem[] = [];
	const cancelledProducts = Object.keys(cancelledItems.value) as ProductType[];
	const downgradedProducts = Object.keys(downgradedItems.value) as ProductType[];
	const relevantDowngradedProducts = downgradedProducts.filter((productType) => !cancelledProducts.includes(productType));
	relevantDowngradedProducts.forEach((productType) => {
		// const productPreviews = downgradedItems.value[productType];
		// const newInvoicePreview = productPreviews.new;
		// const currentInvoicePreview = productPreviews.current;
		// const newAmount = getInvoicePreviewNewAmount(newInvoicePreview);
		// const currentAmount = getInvoicePreviewNewAmount(currentInvoicePreview);
		res.push({
			from: {
				title: `From: ${planNamesByProductType.value[productType].current}`,
				//  price: `-${getReadableAmountInDollars(currentAmount)}`
			},
			to: { title: `To: ${planNamesByProductType.value[productType].new}`, price: getReadableAmountInDollars(0) },
		});
	});
	return res;
});

const cancelledItemsDisplay = computed(() => {
	const res: PriceBreakdownItem[] = [];
	const cancelledProducts = Object.keys(cancelledItems.value) as ProductType[];
	cancelledProducts.forEach((productType) => {
		const productPreviews = cancelledItems.value[productType];
		const currentInvoicePreview = productPreviews.current;
		const currentAmount = getInvoicePreviewNewAmount(currentInvoicePreview);
		res.push({ from: { title: planNamesByProductType.value[productType].current, price: getReadableAmountInDollars(currentAmount) } });
	});
	return res;
});

const priceBreakdownBySection = [
	{
		title: 'Upgraded items',
		items: upgradedItemsDisplay.value,
	},
	{
		title: 'New items',
		items: newItemsDisplay.value,
	},
	{
		title: 'Downgraded items',
		items: downgradedItemsDisplay.value,
		sectionDescription: 'Downgraded items only take affect in the next billing cycle.',
	},
	{
		title: 'Cancelled items',
		items: cancelledItemsDisplay.value,
		sectionDescription: 'Downgraded items only take affect in the next billing cycle.',
	},
].filter((section) => section.items.length > 0);

const close = () => {
	props.dialogPromiseHandlers.resolve();
	AppDialogManagerService.closeCurrentDialog();
};

return { __sfc: true,props, usePricingPreview, billingPeriod, newTeamFeaturePlanId, teamFeaturesPricePreviewInputParams, currentTeamFeaturePlanId, currentTeamFeaturesPricePreviewInputParams, didTeamFeaturesChange, newTeamFeaturesInvoicePreview, currentTeamFeaturesInvoicePreview, newCustomerFeaturePlanId, newCustomerFeatureQuantity, currentCustomerFeaturePlanId, currentCustomerFeatureQuantity, newCustomerFeaturesPricePreviewInputParams, currentCustomerFeaturesPricePreviewInputParams, newCustomerFeaturesInvoicePreview, currentCustomerFeaturesInvoicePreview, didCustomerFeaturesChange, newAutomationQuantity, currentAutomationQuantity, newAutomationPricePreviewInputParams, currentAutomationPricePreviewInputParams, newAutomationInvoicePreview, currentAutomationInvoicePreview, didAutomationChange, newPlyCreditsQuantity, currentPlyCreditsQuantity, newPlyCreditsPricePreviewInputParams, currentPlyCreditsPricePreviewInputParams, newPlyCreditsInvoicePreview, currentPlyCreditsInvoicePreview, didPlyCreditsChange, teamFeaturesPrices, customerFeaturesPrices, automationPrices, plyCreditsPrices, totalDueToday, readableTotalDueToday, allRelevantInvoicePreviews, upgradedItems, getInvoicePreviewNewAmount, newItems, downgradedItems, cancelledItems, planNamesByProductType, upgradedItemsDisplay, newItemsDisplay, downgradedItemsDisplay, cancelledItemsDisplay, priceBreakdownBySection, close, AppDialog, PriceBreakdownSection }
}

})